import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

/**
 * JavaScriptThis Component
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function JavaScriptThis() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const thisModules = useMemo(() => [
        {
            title: "1. The Global Context",
            tag: "Window / Global",
            desc: "When used outside of any function or object, 'this' refers to the global object. In browsers, this is the 'window'. At ProDevConsult, we avoid polluting this space.",
            code: `// Run in a browser console\nconsole.log(this); \n// Output: Window {...}`
        },
        {
            title: "2. Standard Object Methods",
            tag: "Implicit Binding",
            desc: "When a function is called as a method of an object, 'this' is bound to that specific object instance.",
            code: `const leadDev = {\n  name: "Hazzan",\n  greet() {\n    console.log("Hello, I am " + this.name);\n  }\n};\n\nleadDev.greet(); // Output: Hello, I am Hazzan`
        },
        {
            title: "3. The Arrow Function Trap",
            tag: "Lexical Scope",
            desc: "Critical: Arrow functions do not have their own 'this'. They inherit it from the parent scope. This is why 'this.name' often returns undefined here.",
            code: `const proDevStaff = {\n  name: "Hazzan",\n  greet: () => {\n    // Inherits 'this' from the global scope, not proDevStaff\n    console.log("Welcome, " + this.name);\n  }\n};\n\nproDevStaff.greet(); // Output: Welcome, undefined`
        },
        {
            title: "4. Event Listeners",
            tag: "DOM Binding",
            desc: "In a standard event handler function, 'this' automatically points to the element that received the event.",
            code: `const button = document.querySelector(".btn-prodev");\n\nbutton.addEventListener("click", function() {\n  console.log(this.innerHTML); // Refers to the button element\n  this.style.backgroundColor = "goldenrod";\n});`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "goldenrod", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    MASTERING "THIS"
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Execution Context Dynamics
                            </Typography>
                            <IconButton onClick={() => handleSpeak("The value of 'this' depends entirely on how a function is invoked. Mastering this behavior is essential for clean architecture.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7, mb: 3 }}>
                            In JavaScript, <b>`this`</b> is a keyword that refers to the object that is "owning" the current execution. However, its binding is dynamic. Whether you are in a global scope, a method, or an arrow function, <b>Hazzan</b> recommends always verifying the call-site.
                        </Typography>
                    </SectionCard>
                </Grid>

                {thisModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">CONTINUE TO ADVANCED SCOPE</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
